import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { ArrowForward } from "@mui/icons-material";
import { alpha, Box, Checkbox, Stack, Tooltip, Typography } from "@mui/material";
import type { KeyboardEvent } from "react";
import {
  insightMetricRowLayoutSx,
  INSIGHT_PANEL_COMPACT,
  READABLE_BODY_SX,
  analysisBg,
} from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";
import type { InsightChartItem } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

interface InsightSelectableBarListProps {
  items: InsightChartItem[];
  selectedKeys: string[];
  accentColor: string;
  totalForPercent: number;
  maxValue: number;
  patientCountByKey: Record<string, number>;
  itemUnitLabel: string;
  onToggle: (key: string) => void;
  disabled?: boolean;
}

function buildTooltip(
  value: number,
  patientCount: number,
  itemUnitLabel: string
): string {
  return `${value} ${itemUnitLabel} · ${patientCount} pacientes`;
}

export function InsightSelectableBarList({
  items,
  selectedKeys,
  accentColor,
  totalForPercent,
  maxValue,
  patientCountByKey,
  itemUnitLabel,
  onToggle,
  disabled = false,
}: InsightSelectableBarListProps) {
  const safeMax = Math.max(maxValue, 1);

  const handleKeyDown = (event: KeyboardEvent, key: string) => {
    if (disabled) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle(key);
    }
  };

  return (
    <Stack spacing={1.25}>
      {items.map((item) => {
        const selected = selectedKeys.includes(item.key);
        const pct =
          totalForPercent > 0 ? Math.round((item.value / totalForPercent) * 100) : 0;
        const patients = patientCountByKey[item.key] ?? 0;
        const barWidth = `${Math.max(8, (item.value / safeMax) * 100)}%`;

        return (
          <Tooltip
            key={item.key}
            title={buildTooltip(item.value, patients, itemUnitLabel)}
            arrow
            placement="top"
          >
            <Box
              component="button"
              type="button"
              aria-pressed={selected}
              onClick={() => !disabled && onToggle(item.key)}
              onKeyDown={(e) => handleKeyDown(e, item.key)}
              sx={{
                ...insightMetricRowLayoutSx,
                display: "flex",
                width: "100%",
                textAlign: "left",
                border: "1px solid",
                borderColor: selected ? accentColor : alpha(accentColor, 0.35),
                borderRadius: 2,
                px: 1.5,
                py: 1.15,
                minHeight: 52,
                [INSIGHT_PANEL_COMPACT]: { px: 1.25, py: 1 },
                cursor: disabled ? "default" : "pointer",
                pointerEvents: disabled ? "none" : "auto",
                opacity: disabled ? 0.45 : 1,
                bgcolor: selected ? accentColor : "background.paper",
                color: selected ? "#fff" : "text.primary",
                font: "inherit",
                transition:
                  "opacity 0.15s ease, background-color 0.15s ease, border-color 0.15s ease",
                "&:hover": {
                  borderColor: accentColor,
                  bgcolor: selected ? accentColor : analysisBg(accentColor, 0.1),
                },
                "&:focus-visible": {
                  outline: `2px solid ${alpha(accentColor, 0.5)}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Checkbox
                size="small"
                checked={selected}
                tabIndex={-1}
                icon={
                  <CheckBoxOutlineBlankIcon
                    sx={{ fontSize: 24, color: selected ? "#fff" : alpha(accentColor, 0.55) }}
                  />
                }
                checkedIcon={
                  <CheckBoxIcon sx={{ fontSize: 24, color: selected ? "#fff" : accentColor }} />
                }
                sx={{ p: 0, color: "inherit" }}
                disableRipple
              />
              <Typography
                className="insight-metric-label"
                title={item.label}
                sx={{ color: "inherit", ...READABLE_BODY_SX }}
              >
                {item.label}
              </Typography>
              <Box
                className="insight-metric-bar-wrap"
                sx={{
                  height: 12,
                  borderRadius: 6,
                  bgcolor: selected ? alpha("#fff", 0.25) : alpha(accentColor, 0.12),
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: barWidth,
                    borderRadius: 6,
                    bgcolor: selected ? "#fff" : alpha(accentColor, 0.55),
                  }}
                />
              </Box>
              <Typography
                className="insight-metric-pct"
                fontWeight={700}
                sx={{ width: 52, textAlign: "right", color: "inherit", ...READABLE_BODY_SX }}
              >
                {pct}%
              </Typography>
              {selected ? (
                <ArrowForward
                  className="insight-metric-arrow"
                  sx={{ fontSize: 22, color: "#fff", flexShrink: 0 }}
                />
              ) : (
                <Box className="insight-metric-arrow" sx={{ width: 22, flexShrink: 0 }} />
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
}
